import type { VocabularySnapshotV2 } from "../../versions/v2.ts";
import type { VocabularySnapshotV3 } from "../../versions/v3.ts";
import type { MigrationContext } from "../types.ts";

function initialCardSchedule(dueAt: string) {
  return {
    stage: 0 as const,
    dueAt,
    lastAssessmentAt: null,
    lastPresentedAt: null,
    presentationOrdinal: null,
  };
}

/**
 * Adds two card schedules per meaning, both due at the captured migration time.
 * Preserves written stages, due dates, exposure, assessments, and event history.
 * Does not invent card grades or copy written progress onto either card direction.
 * Legacy events gain an origin: exercise when they reference an exercise, otherwise legacy.
 */
export const v2ToV3Step = {
  id: "v2-to-v3-card-schedules",
  fromVersion: 2,
  toVersion: 3,
  description: "Add card schedules due now without inferring card progress.",
  migrate(source: unknown, context: MigrationContext): VocabularySnapshotV3 {
    const snapshot = source as VocabularySnapshotV2;
    return {
      schemaVersion: 3,
      dataRevision: snapshot.dataRevision,
      schedulerState: {
        schedulerVersion: snapshot.schedulerState.schedulerVersion,
        lastPracticedMeaningId: snapshot.schedulerState.lastPracticedMeaningId,
        lastObservedAt: snapshot.schedulerState.lastObservedAt,
        cardPolicyVersion: 1,
        cardPresentationCounters: { hide_meaning: 0, hide_word: 0 },
        lastPresentedCardMeaningIds: { hide_meaning: null, hide_word: null },
      },
      words: snapshot.words,
      meanings: snapshot.meanings,
      relations: snapshot.relations,
      exerciseSets: snapshot.exerciseSets,
      practiceStates: snapshot.practiceStates.map((state) => ({
        ...state,
        cardSchedules: {
          hide_meaning: initialCardSchedule(context.migratedAt),
          hide_word: initialCardSchedule(context.migratedAt),
        },
      })),
      reviewHistory: snapshot.reviewHistory.map((event) => ({
        ...event,
        format: "written" as const,
        origin: event.exerciseId ? ("exercise" as const) : ("legacy" as const),
      })),
      historyTruncated: snapshot.historyTruncated,
    };
  },
};
